import { AxiosError, isAxiosError } from 'axios';

import { EmailToChangePasswordResponse } from '@/types/editProfile';
import {
  showErrorToast,
  reportError,
  URLs,
  axiosErrorHandler,
  isAndroid,
  reportErrorServer,
} from '@/utils';
import axiosGdt, { axiosGdtWithoutCredentials } from '@/utils/axiosGdt';

// ----------------------------------------------------
// EDIT PROFILE - enviar mail de cambio de password
// ----------------------------------------------------
type ClientConfig = {
  client?: {
    domain?: string;
    clientId?: string;
  };
  userInfo?: {
    email?: string;
    sub?: string;
  };
};
const clientConfig: ClientConfig = {};

/*
Lo puede necesitar enviar password reset
*/
export function rememberClient(
  clientInfo: Exclude<ClientConfig['client'], undefined>
) {
  clientConfig.client = clientInfo;
}

export function rememberUser(
  userInfo: Exclude<ClientConfig['userInfo'], undefined>
) {
  clientConfig.userInfo = userInfo;
}

type ChangePasswordJSResponseError = {
  error?: string;
};
type ChangePasswordJSResponse = string | ChangePasswordJSResponseError;

const txtErrorChangePwd =
  'No es posible enviar el email de cambio de password.' +
  'Puede usar el link de "¿Olvidaste tu contraseña?" en el inicio de sesión, ' +
  'debajo del ingreso de contraseña';

/*
 * EDIT PROFILE - enviar mail de cambio de password
 * CLIENT SIDE - usando JS
 * documentation:
 * https://auth0.com/docs/api/authentication/change-password/change-password
 */
const sendEmailToChangePasswordJS = axiosErrorHandlerResetPwd(async () => {
  if (
    !clientConfig.client ||
    !clientConfig.userInfo ||
    !clientConfig.userInfo?.sub?.startsWith('auth0|')
  ) {
    throw new Error(txtErrorChangePwd);
  }

  const url = `https://${clientConfig.client?.domain}/dbconnections/change_password`;
  const response =
    await axiosGdtWithoutCredentials.post<ChangePasswordJSResponse>(
      url,
      {
        client_id: clientConfig.client?.clientId,
        email: clientConfig.userInfo?.email,
        connection: 'Pase',
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
  return response.data;
});

function axiosErrorHandlerResetPwd<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  customErrorMessage?: string
) {
  return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    try {
      return await fn(...args);
    } catch (error) {
      if (isAxiosError<ChangePasswordJSResponseError>(error)) {
        const err = error as AxiosError<ChangePasswordJSResponseError, any>;
        showErrorToast({
          estado: false,
          mensaje: err.response?.data?.error ?? txtErrorChangePwd,
        });
        if (error.response?.data?.error) {
          throw new Error(txtErrorChangePwd);
        } else {
          reportError('AxiosError - ' + error?.config?.url, error as Error);
          throw new Error(txtErrorChangePwd);
        }
      } else if (error instanceof Error) {
        showErrorToast({
          codError: -100,
          mensaje: error.message,
          estado: false,
        });
        reportError('Error' + error.message, error as Error);
        throw new Error(error.message);
      } else {
        showErrorToast({
          codError: -100,
          mensaje: txtErrorChangePwd,
          estado: false,
        });
        reportError('Error desconocido en axiosErrorHandler', error as Error);
        throw new Error(customErrorMessage ?? txtErrorChangePwd);
      }
    }
  };
}

/**
 * EDIT PROFILE - enviar mail de cambio de password
 * SERVER SIDE
 */
const sendEmailToChangePasswordServer = axiosErrorHandler(async () => {
  const url = URLs.PROFILE.SEND_EMAIL_TO_CHANGE_PASSWORD;
  const response = await axiosGdt.post<EmailToChangePasswordResponse>(url);
  return response.data;
});

export const sendEmailToChangePassword = async function () {
  if (isAndroid()) {
    /* 
    Para la versión >177 de android voy a hacer el envío desde el cliente, 
    y para eso necesito que la app comparta información del usuario: 
    cookieStore.get('android_wvAuth').then((cookie) => {
      // eslint-disable-next-line no-console
      console.log(
        'userInfo:',
        cookie?.value &&
          window.JSBridge?.getInformation(cookie?.value, 'userInfo')
      );
    });
    */
    // return { estado: false, mensaje: txtErrorChangePwd };
    const response = await sendEmailToChangePasswordServer();
    return response;
  }

  const response2 = await sendEmailToChangePasswordJS();
  if (typeof response2 !== 'string') {
    reportErrorServer(
      'Error al enviar email de cambio de password: ' +
        clientConfig.userInfo?.email +
        '- resp: ' +
        JSON.stringify(response2)
    );
    return {
      estado: false,
      mensaje: txtErrorChangePwd,
    };
  } else {
    return { estado: true };
  }
};
